import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Analytics } from '@reef/core';
import { Observable } from 'rxjs';
import { SessionInformationComponent } from './session-information/session-information.component';
import { LocalesService, SupportedLocale } from './services/locales/locales.service';
import { AlertService } from './services/alert/alert.service';
import { UtilService } from './services/util/util.service';
import { AlertModel } from './model/alert.model';
import { CookiePolicyComponent } from './cookie-policy/cookie-policy.component';
import { ClpInitConfigExtended } from './model/clpInitConfigExtended.model';
import { selectClpConfig } from './redux/core.reducer';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

const MAS_SUPPORT_PATH = 'https://myamadeus-services.amadeus.com/csm?id=kb_article&sysparm_article=KB0026804';
const ASH_SUPPORT_PATH = 'https://servicehub.amadeus.com/c/portal/view-solution/757691800';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
  host: { class: 'h-100 d-flex flex-column' }
})
export class AppComponent {
  private readonly localesService = inject(LocalesService);
  private readonly alertService = inject(AlertService);
  private readonly utilService = inject(UtilService);
  readonly router = inject(Router);
  private readonly modalService = inject(NgbModal);
  private readonly store = inject(Store);
  private readonly analytics = inject(Analytics);
  private hugMas = false;

  availableLanguages: { languageName: string; locale: SupportedLocale }[];
  currentLanguage: { languageName: string; locale: SupportedLocale };
  globalAlerts$: Observable<AlertModel[]>;
  clpConfig$: Observable<ClpInitConfigExtended | undefined>;
  displayFooter: boolean;

  constructor() {
    this.availableLanguages = this.localesService.getAvailableLanguages();
    this.currentLanguage = {
      locale: this.localesService.getDefaultLocaleUsed(),
      languageName: this.localesService.getLanguageName()
    };
    this.globalAlerts$ = this.alertService.getAlerts();

    this.clpConfig$ = this.store.select(selectClpConfig);
    this.displayFooter = this.isNotSso();
    this.clpConfig$.subscribe((config) => {
      if (config?.loginParameters?.hugMas) {
        this.hugMas = true;
      }
    });
  }

  selectLanguage(locale: SupportedLocale): void {
    this.localesService.updatePreferedLocaleCookie(locale, true);
  }

  goToSupport(): void {
    this.utilService.redirect(this.hugMas ? MAS_SUPPORT_PATH : ASH_SUPPORT_PATH);
    this.analytics.track({ action: 'login.requestSupport' });
  }

  goToContactLink(): void {
    this.clpConfig$.subscribe((config) => {
      if (config) {
        const contactUrl = config.loginParameters.contactUrl;
        if (contactUrl) {
          this.utilService.redirect(contactUrl);
        }
      }
    });
  }

  goToPrivacyPolicyLink(): void {
    this.clpConfig$.subscribe((config) => {
      if (config) {
        const privacyPolicyUrl = config.loginParameters.privacyPolicyUrl;
        if (privacyPolicyUrl) {
          this.utilService.redirect(privacyPolicyUrl);
        }
      }
    });
  }

  openSecurityInformationModal(): void {
    this.modalService.open(SessionInformationComponent, { centered: true });
  }

  public openLegalNotices(): void {
    this.modalService.open(CookiePolicyComponent, { centered: true, size: 'lg' });
  }

  private isNotSso(): boolean {
    return (
      window.location.pathname !== '/login/loginFromSAMLSSO' && window.location.pathname !== '/login/redirectSAMLSSO'
    );
  }
}
